/**
 * ImportInformacoesModal - Modal para importar pós-contagem por cliente via Excel
 *
 * Epic Redesign Pós-Contagem:
 * - Formato atualizado com 9 colunas incluindo Cliente
 * - Uma linha por cliente
 * - Info colapsável ao clicar no ícone
 */
import React, { useState } from 'react';
import { X, Upload, FileText, Info, ChevronDown, ChevronUp } from 'lucide-react';

// Column definitions for the collapsible info
const POS_CONTAGEM_COLUMNS = [
  { col: 'A', name: 'Cliente', required: true, description: 'CNPJ ou Nome Fantasia' },
  { col: 'B', name: 'Locais Contados', required: false, description: 'texto livre (vírgula separa)' },
  { col: 'C', name: 'Lacres', required: false, description: 'texto livre (vírgula separa)' },
  { col: 'D', name: 'O.C. (Ordem de Coleta)', required: false, description: 'texto livre' },
  { col: 'E', name: 'Início Contagem', required: false, description: 'DD/MM/YYYY HH:MM' },
  { col: 'F', name: 'Término Contagem', required: false, description: 'DD/MM/YYYY HH:MM' },
  { col: 'G', name: 'Envio Relatório', required: false, description: 'DD/MM/YYYY HH:MM' },
  { col: 'H', name: 'Observação', required: false, description: 'texto livre' },
  { col: 'I', name: 'Status', required: false, description: 'PENDENTE, EM_ANDAMENTO, CONCLUIDO, CANCELADO' },
];

interface ImportInformacoesModalProps {
  selectedInformacoesFile: File | null;
  uploadingInformacoes: boolean;
  setSelectedInformacoesFile: (file: File | null) => void;
  handleImportInformacoesExcel: () => void;
  closeImportInformacoesModal: () => void;
}

export const ImportInformacoesModal: React.FC<ImportInformacoesModalProps> = ({
  selectedInformacoesFile,
  uploadingInformacoes,
  setSelectedInformacoesFile,
  handleImportInformacoesExcel,
  closeImportInformacoesModal,
}) => {
  const [showInfo, setShowInfo] = useState(false);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedInformacoesFile(file);
    }
  };

  const handleClose = () => {
    setSelectedInformacoesFile(null);
    closeImportInformacoesModal();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-lg w-full">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-500 to-teal-500 px-6 py-4 rounded-t-xl flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Upload className="text-white" size={24} />
            <h3 className="text-lg font-semibold text-white">
              Importar Pós-Contagem
            </h3>
          </div>
          <button
            onClick={handleClose}
            disabled={uploadingInformacoes}
            className="text-white hover:bg-white/20 rounded-lg p-1 transition disabled:opacity-50"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* File Input */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Selecione o arquivo Excel:
            </label>
            <div className="flex items-center gap-3">
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileSelect}
                disabled={uploadingInformacoes}
                className="flex-1 text-sm text-gray-600 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-emerald-50 dark:file:bg-emerald-900/20 file:text-emerald-700 dark:file:text-emerald-400 hover:file:bg-emerald-100 dark:hover:file:bg-emerald-900/30 disabled:opacity-50 cursor-pointer"
              />
            </div>
            {selectedInformacoesFile && (
              <div className="flex items-center gap-2 mt-2 text-sm text-gray-600 dark:text-gray-400">
                <FileText size={16} />
                <span className="font-medium">{selectedInformacoesFile.name}</span>
                <span className="text-xs text-gray-500">
                  ({(selectedInformacoesFile.size / 1024).toFixed(2)} KB)
                </span>
              </div>
            )}
          </div>

          {/* Upload Progress */}
          {uploadingInformacoes && (
            <div className="bg-emerald-50 dark:bg-emerald-900/20 border-l-4 border-emerald-500 p-4 rounded">
              <div className="flex items-center gap-3">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-emerald-600"></div>
                <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">
                  Processando arquivo...
                </span>
              </div>
            </div>
          )}

          {/* Collapsible Info Panel */}
          {showInfo && (
            <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 p-4 rounded-lg animate-in slide-in-from-top-2 duration-200">
              <h4 className="text-sm font-semibold text-emerald-800 dark:text-emerald-200 mb-2">
                Formato Esperado - 9 Colunas
              </h4>
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
                <strong>Uma linha por cliente:</strong> Cada linha representa os dados de pós-contagem de um cliente.
              </p>
              <div className="grid grid-cols-1 gap-1 text-xs">
                {POS_CONTAGEM_COLUMNS.map((col) => (
                  <div
                    key={col.col}
                    className="flex items-center gap-2 text-gray-700 dark:text-gray-300"
                  >
                    <span className={`px-2 py-0.5 rounded font-mono font-bold ${
                      col.required
                        ? 'bg-emerald-100 dark:bg-emerald-800 text-emerald-700 dark:text-emerald-200'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                    }`}>
                      {col.col}
                    </span>
                    <span className={col.required ? 'font-medium' : ''}>
                      {col.name}
                      {col.required && <span className="text-red-500 ml-0.5">*</span>}
                    </span>
                    <span className="text-gray-500 dark:text-gray-400 italic">- {col.description}</span>
                  </div>
                ))}
              </div>
              <div className="mt-3 pt-2 border-t border-emerald-200 dark:border-emerald-700">
                <p className="text-xs text-gray-600 dark:text-gray-400 italic">
                  * Campos obrigatórios. A primeira linha (cabeçalho) será ignorada.
                </p>
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                  <strong>⚠ Atenção:</strong> Ao importar, os dados de pós-contagem anteriores serão substituídos.
                </p>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={handleClose}
              disabled={uploadingInformacoes}
              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-lg transition disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              onClick={() => setShowInfo(!showInfo)}
              className={`flex items-center gap-2 px-4 py-2 border-2 rounded-lg transition font-medium ${
                showInfo
                  ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300'
                  : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20'
              }`}
              title="Ver formato esperado"
            >
              <Info size={18} />
              {showInfo ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
            <button
              onClick={handleImportInformacoesExcel}
              disabled={!selectedInformacoesFile || uploadingInformacoes}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-lg hover:from-emerald-600 hover:to-teal-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Upload size={18} />
              {uploadingInformacoes ? 'Importando...' : 'Importar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
